import { useTranslation } from "react-i18next";

import type { TimelineCardView } from "../../services/generated/client.js";
import { cn } from "../../lib/utils.js";
import { Card } from "./Card.js";
import { CardCount } from "./CardCount.js";
import type { Scale } from "./SideBoard.js";

/**
 * A side's cards, oldest first, with the gaps between them as the placement
 * control.
 *
 * **This is the component the whole game rests on.** Everything else is a way of
 * earning the right to use it.
 *
 * # A slot is a gap, and there is always one more slot than card
 *
 * `n` cards make `n + 1` places to put the next one — before the first, between
 * each pair, after the last. An empty timeline therefore has exactly one slot,
 * and that slot is always correct, which is why the first round of a game is
 * never lost. Rendering the gaps as the buttons rather than the cards is what
 * makes "put it here" a single tap instead of a drag, and a drag is miserable
 * on a phone being passed around a room.
 *
 * # Hidden is not empty
 *
 * `blind_timeline` hides *which* cards a side holds, never *how many* — how
 * close somebody is to winning is public, and a board that could not show it
 * would be unreadable. So a hidden timeline renders `cardCount` backs.
 *
 * # `claimedSlot` renders even when the timeline is read-only — phase 16a
 *
 * A `Slot` collapses to an invisible rule when it is not `placeable`, which is
 * correct for an idle board and was the whole bug in challenge: a non-active
 * player saw a timeline with **no gaps at all**, and was then asked whether they
 * contested a placement that had never been drawn. `claimedSlot` is the one gap
 * that draws itself regardless, because it is the thing being contested.
 *
 * It is a **claim, not the answer**. The years already on this timeline are
 * face-up; the card in flight is still secret. Marking a gap says only where the
 * active side asserts it belongs.
 */
/**
 * A gap somebody else has already claimed, and who claimed it.
 *
 * `by` is `null` when the roster cannot name them — they left the party — which
 * the screen says rather than inventing a name.
 */
export type TakenSlot = {
  /** The gap, indexed as the timeline counts them. */
  slot: number;
  /** Who claimed it, or `null` if the roster no longer knows. */
  by: string | null;
};

export function Timeline({
  cards,
  cardCount,
  hidden = false,
  onPlace,
  selectedSlot = null,
  claimedSlot = null,
  takenSlots,
  claimedBy = null,
  challengedTrack = null,
  revealedTrack = null,
  cardsToWin,
  label,
  scale = "near",
  className,
}: {
  /** The cards, oldest first. Empty when `hidden`. */
  cards: readonly TimelineCardView[];
  /** How many cards this side holds — accurate even when `hidden`. */
  cardCount?: number;
  /** Whether `blind_timeline` is hiding the cards from this viewer. */
  hidden?: boolean;
  /** Place into a slot. Absent means this timeline is read-only. */
  onPlace?: (slot: number) => void;
  /** Which slot is chosen, if any. */
  selectedSlot?: number | null;
  /**
   * The slot the active side claims the card belongs in, from the challenge
   * window onward. Drawn even on a read-only timeline — see the note above.
   */
  claimedSlot?: number | null;
  /**
   * Gaps another challenger has already claimed — phase 16b.
   *
   * One claim per slot is the contention rule, so a challenger who cannot see
   * which gaps are gone is aiming blind and will simply be refused. Drawn, and
   * not offered as a target.
   *
   * *Who* claimed one is deliberately not here: the rule turns on the slot being
   * taken, not on who took it.
   */
  takenSlots?: readonly TakenSlot[];
  /**
   * Who the `claimedSlot` belongs to, for the sentence on it.
   *
   * `null` falls back to *"they"* — a spectator surface with no roster, or a
   * side nobody can name any more.
   */
  claimedBy?: string | null;
  /** A card somebody has contested, by track id. */
  challengedTrack?: string | null;
  /** A card that just landed, by track id. */
  revealedTrack?: string | null;
  /** Cards on a completed timeline — draws the progress readout. */
  cardsToWin?: number;
  /** Whose timeline this is, for the accessible name. */
  label?: string;
  /** How far away the reader is — sizes the progress readout. */
  scale?: Scale;
  className?: string;
}) {
  const { t } = useTranslation();
  const held = cardCount ?? cards.length;
  // Slots come from the *cards*, never from `cardCount`: a hidden timeline
  // cannot be placed into by the viewer, and offering slots computed from a
  // count would let a player aim at gaps they cannot see.
  const slots = cards.length + 1;
  const placeable = Boolean(onPlace) && !hidden;

  return (
    <section
      data-testid="timeline"
      aria-label={label ?? t("timeline.label")}
      className={cn("min-w-0", className)}
    >
      {(label || cardsToWin !== undefined) && (
        <header className="mb-2 flex items-center justify-between gap-3 text-xs text-white/60">
          {label && <span className="truncate font-medium text-white/80">{label}</span>}
          {/* 🔴 **The count is drawn, not spelled** — and it used to be *both*.
              A `SideBoard` header read *"3 cards"* on the left and *"3 / 10"* on
              the right: the same number twice, in two grammars, once as a word
              beside a coin that was already a coin. The card carries it now, and
              the denominator is the only text left. */}
          {/* ⚠️ `ml-auto`, not `justify-between`. `SideBoard` stopped passing a
              label when the count stopped being a word, and a lone child of a
              `justify-between` row sits at the **start** — so the progress
              drifted under the name instead of holding the right edge. */}
          {cardsToWin !== undefined && (
            <span className="ml-auto flex shrink-0 items-center gap-1.5">
              {/* One sentence for a screen reader, and the drawing for
                  everybody else: read aloud, the parts would otherwise arrive
                  as "3 cards … slash 10 … cards towards winning". */}
              <span aria-hidden="true" className="flex items-center gap-1.5">
                <CardCount count={held} size={scale === "room" ? "room" : "tight"} />
                <span className="tabular-nums text-white/45">/ {cardsToWin}</span>
              </span>
              <span className="sr-only">
                {t("timeline.progress", { held, total: cardsToWin })}
              </span>
            </span>
          )}
        </header>
      )}

      {/* 🔴 **A grid of cards, and it never scrolls sideways** — findings 3.5
          and 3.13, finished in 20d's third pass.

          The original argument here was that *"chronological order is only
          readable as a line, and a timeline that wraps to a second row stops
          being one"*. The fourth party settled it the other way: reaching a slot
          by scrolling is what nobody could do under a clock, and a control you
          have to scroll to is a lost round.

          📌 A wrapped rail does not lose its direction, because this is not bare
          cards — it is gap, card, gap, card, gap, and the slots before the first
          and after the last are *drawn*, so the ends are marked rather than
          inferred. Reading order is left-to-right, top-to-bottom, like every
          other block on the phone.

          ## Two layouts, because a gap is two different things

          | | |
          |---|---|
          | **placeable** | a `grid`: every gap is a target, so every cell is a card-sized tile and the rows fill |
          | read-only | a wrapped row: the gaps collapse to hairlines, because a control that does nothing most of the time trains people to ignore it |

          The grid is the maintainer's call — *"make the placeholders the same
          size as the cards so we can have a unified experience and not those big
          and small gaps between them, the idea is to be filling like a grid of
          cards"*. `auto-fill` with `minmax(80px, 1fr)` is what makes every tile
          the same width and every row full, at any container width, without this
          file knowing how wide a phone is.

          ⚠️ **It gets taller, and that is accepted**: *"no problem if we need to
          scroll down when the timeline is too big, it's ok, but that's an
          exception and it's better than scrolling right or left."* Card-sized
          gaps roughly double the tile count — ten cards is twenty-one tiles — so
          a late-game board will overflow the stage band on a small phone. The
          band scrolls; the header and the action bar do not (see `RoundPanel`).

          ⚠️ **Every child is its own item** — the `li` around a slot and its card
          is `display: contents` — so a cell is always exactly one slot or one
          card, never a pair sharing a track. */}
      <ol
        className={cn(
          "gap-1 pb-2",
          placeable
            ? // 🔴 **The deck scrolls, the app does not** — the maintainer's
              // call: *"put all the deck in a container so we scroll only
              // inside the container and the app doesn't get scrolled too."*
              // A long timeline is the one thing on this screen allowed to
              // overflow, so it is the one thing that carries a scrollbar —
              // the disc and the instruction do not move for it.
              //
              // ⚠️ **No `overscroll-contain`, and that was a real bug.** It was
              // here for one pass and it *trapped the gesture*: this is a
              // scroller nested inside the stage band, and on a 360×780 phone
              // the challenge stage is a little taller than the band. Once the
              // deck hit its own end, containment refused to chain the scroll
              // outward, so the band never moved and the last row stayed under
              // the action bar — *"I can't scroll all the way down, the footer
              // panel gets on top."* Chaining is the behaviour that makes two
              // nested scrollers usable; the cap is what stops the deck eating
              // the screen, and the cap is the part worth keeping.
              //
              // ⚠️ A viewport fraction, not a pixel height: this renders on a
              // 360px phone and on a 1440px desktop, and the cap has to leave
              // room for the disc above it on both.
              "grid max-h-[46vh] overflow-y-auto"
            : // Centred — finding 3.3. The opening card sat against the left
              // edge of a mostly empty rail, which reads as a row that has
              // already scrolled rather than as the start of a timeline.
              "flex flex-wrap items-stretch justify-center gap-y-2",
        )}
        style={
          placeable
            ? { gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))" }
            : undefined
        }
        data-testid="timeline-rail"
      >
        {hidden
          ? Array.from({ length: held }, (_, index) => (
              <li key={index}>
                <div
                  data-testid="timeline-card-back"
                  aria-label={t("timeline.hiddenCard")}
                  className="h-20 w-20 shrink-0 rounded-xl border border-dashed border-white/15 bg-stage-900/60"
                />
              </li>
            ))
          : cards.map((card, index) => (
              <li key={card.track} className="contents">
                <Slot
                  index={index}
                  placeable={placeable}
                  tile={placeable}
                  selected={selectedSlot === index}
                  claimed={claimedSlot === index}
                  taken={takenSlots?.find((claim) => claim.slot === index) ?? null}
                  claimedBy={claimedBy}
                  onPlace={onPlace}
                  before={card.year}
                  after={cards[index - 1]?.year}
                />
                <Card
                  compact
                  // In the grid every cell is one tile: the card gives up its
                  // fixed width to the column, and its height to the row.
                  {...(placeable ? { className: "h-full w-full" } : {})}
                  artist={card.artist}
                  title={card.title}
                  year={card.year}
                  state={
                    card.track === challengedTrack
                      ? "challenged"
                      : card.track === revealedTrack
                        ? "revealed"
                        : "placed"
                  }
                />
              </li>
            ))}

        {/* The last gap. On an empty timeline it is the only one — and it is
            always the right answer, which is why nobody loses round one. */}
        {!hidden && (
          <li>
            <Slot
              index={slots - 1}
              placeable={placeable}
              tile={placeable}
              selected={selectedSlot === slots - 1}
              claimed={claimedSlot === slots - 1}
              taken={takenSlots?.find((claim) => claim.slot === slots - 1) ?? null}
              claimedBy={claimedBy}
              onPlace={onPlace}
              after={cards[cards.length - 1]?.year}
            />
          </li>
        )}
      </ol>

      {held === 0 && !placeable && (
        <p className="text-xs text-white/40">{t("timeline.empty")}</p>
      )}
    </section>
  );
}

/**
 * One gap between two cards.
 *
 * When nothing can be placed it is a thin rule rather than a button: an
 * always-visible control that does nothing most of the time trains people to
 * ignore it by the phase where it matters.
 */
function Slot({
  index,
  placeable,
  tile,
  selected,
  claimed,
  taken,
  claimedBy,
  onPlace,
  before,
  after,
}: {
  index: number;
  placeable: boolean;
  /** Whether this is a cell in the placeable grid rather than a gap in a row. */
  tile: boolean;
  selected: boolean;
  /** Whether the active side says the card in flight belongs in this gap. */
  claimed: boolean;
  /** The claim another challenger has already made here, if any. */
  taken: TakenSlot | null;
  /** Who made the active side's claim, for the sentence on it. */
  claimedBy: string | null;
  onPlace: ((slot: number) => void) | undefined;
  /** The year of the card to the right, if any. */
  before?: number | undefined;
  /** The year of the card to the left, if any. */
  after?: number | undefined;
}) {
  const { t } = useTranslation();

  // Spoken as a place in time rather than as a number, because "slot 3" means
  // nothing to somebody holding a phone: "after 1984, before 1991" is the
  // decision they are actually making.
  const where = (prefix: "timeline.slot" | "timeline.claim") => {
    // **Named, since 20d.** *"The claimed one should be '{Username} says it
    // goes…'"* — a claim is somebody's move, and the screen was saying "they"
    // about a person sitting in the room. `who` falls back to a pronoun for a
    // surface with no roster.
    const who = claimedBy ?? t("timeline.claim.somebody");
    return after !== undefined && before !== undefined
      ? t(`${prefix}.between`, { after, before, who })
      : after !== undefined
        ? t(`${prefix}.after`, { after, who })
        : before !== undefined
          ? t(`${prefix}.before`, { before, who })
          : t(`${prefix}.here`, { who });
  };

  // ⚠️ Phase 16a: this branch runs **before** the read-only bail-out below, so a
  // claimed gap is drawn on a timeline nobody can place into. That is the entire
  // point — the people who need to see it are exactly the ones who cannot place.
  if (claimed) {
    return (
      <div
        data-testid="timeline-slot-claimed"
        data-slot={index}
        role="img"
        aria-label={where("timeline.claim")}
        className={cn(
          // **Card-sized, not gap-sized** — phase 18, finding #16: *"the card
          // placeholder is too small — it should be like the other cards so it
          // is easy to see."* Measured at `w-9` (36px) against a placed card's
          // `h-28 w-28` (112px at the time; `h-20 w-20` since finding 3.4 —
          // this tracks the card and must keep tracking it), so the card you had
          // just committed to the board was drawn a third as wide as everything
          // already on it.
          //
          // ⚠️ **A slot does double duty and only one of the two was wrong.** An
          // *unclaimed* gap is an insertion point you choose by reading its
          // neighbours, and narrow is correct there — a full-width gap would
          // push apart the very years being compared. A gap that now **holds
          // something** has stopped being a gap.
          "flex flex-col items-center justify-center gap-1 self-stretch p-2",
          tile ? "min-h-20 w-full" : "w-20 shrink-0",
          "rounded-lg border-2 border-flash-500 bg-flash-500/25",
          // No pulse or transition: the display renders this on an underpowered
          // TV browser, where an animated idle loop is the thing to avoid.
          "text-flash-500",
        )}
      >
        <span aria-hidden="true" className="text-2xl leading-none">
          ?
        </span>
        {/* 📌 The range the claim asserts, which is what makes it concrete —
            and, with the exact-year bonus (#15), the thing a challenger is
            actually arguing with.

            ⚠️ **Three words, not a sentence.** It read *"Ana says it goes
            between 1987 and 1995"*, which is five lines inside an 80px tile:
            *"this is a quick game, people need to read fast and act fast."* The
            years are the argument and the name is who is making it; every other
            word was scaffolding. */}
        <span className="text-center text-xs font-medium leading-tight text-flash-500/80">
          {where("timeline.claim")}
        </span>
      </div>
    );
  }

  // Also before the read-only bail-out, and for the same reason: a challenger
  // has to see which gaps are gone, or the one-claim-per-slot rule is invisible
  // and they are simply refused.
  if (taken) {
    const takenLabel = taken.by
      ? t("round.challenge.takenBy", { who: taken.by })
      : t("round.challenge.taken");
    return (
      <div
        data-testid="timeline-slot-taken"
        data-slot={index}
        role="img"
        aria-label={takenLabel}
        className={cn(
          // Card-sized for the same reason as a claim: it *is* a claim, made by
          // somebody else. At 36px a contest was nearly invisible even on the
          // one screen that drew it (#16, and it compounds #9).
          "flex flex-col items-center justify-center gap-1 self-stretch p-2",
          tile ? "min-h-20 w-full" : "w-20 shrink-0",
          "rounded-lg border-2 border-white/25 bg-white/5 text-white/40",
        )}
      >
        <span aria-hidden="true" className="text-2xl leading-none">
          ×
        </span>
        <span className="text-center text-[11px] leading-tight">{takenLabel}</span>
      </div>
    );
  }

  if (!placeable || !onPlace) {
    return <div aria-hidden="true" className="w-1 shrink-0" />;
  }

  const description = where("timeline.slot");

  return (
    <button
      type="button"
      data-testid="timeline-slot"
      data-slot={index}
      aria-pressed={selected}
      onClick={() => onPlace(index)}
      className={cn(
        "self-stretch rounded-lg border-2 border-dashed transition-colors",
        // 🔴 **Card-sized in the grid.** A 36px gap between 80px cards is what
        // the maintainer saw as *"those big and small gaps"* — the row read as
        // debris rather than as a board, and the thing you are being asked to
        // aim at was the smallest thing on it.
        tile ? "min-h-20 w-full" : "w-9 shrink-0",
        selected
          ? "border-flash-500 bg-flash-500/20"
          : "border-white/20 hover:border-flash-500/60",
      )}
    >
      <span className="sr-only">{description}</span>
      <span
        aria-hidden="true"
        className={cn("text-white/40", tile ? "text-2xl" : "text-lg")}
      >
        +
      </span>
    </button>
  );
}
